import { useState, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type RowSelectionState,
  type ColumnSizingState,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/hooks/use-permissions.js';
import { useIsTableMobile } from '@/hooks/use-is-table-mobile.js';
import { useColumnVisibility } from '@/hooks/use-column-visibility.js';
import { useTableSearch } from '@/hooks/use-table-search.js';
import { DataTablePagination } from './data-table-pagination.js';
import { DataTableToolbar } from './data-table-toolbar.js';
import { DataTableMobileCard } from './data-table-mobile-card.js';
import type {
  DataTablePaginationState,
  DataTableSortingState,
  DataTableEmptyState,
} from './types.js';

function DataTableEmptyContent({ state }: { state: DataTableEmptyState }) {
  return (
    <div className="flex flex-col items-center gap-2">
      {state.icon && (
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-muted/50 mb-1">
          {state.icon}
        </div>
      )}
      <p className="text-sm font-semibold text-foreground">{state.title}</p>
      {state.description && <p className="text-xs text-muted-foreground">{state.description}</p>}
      {state.action && <div className="mt-3">{state.action}</div>}
    </div>
  );
}

interface DataTableProps<TData> {
  columns: ColumnDef<TData>[];
  data: TData[];
  isLoading?: boolean;
  isFetching?: boolean;
  pagination: DataTablePaginationState;
  sorting?: DataTableSortingState;
  emptyState: DataTableEmptyState;
  enableRowSelection?: boolean;
  onRowSelectionChange?: (selectedRows: TData[]) => void;
  /** Typed extensibility hook for future filter bar — not rendered yet. */
  filters?: Record<string, unknown>;
  getRowId?: (row: TData) => string;
  onRowClick?: (row: TData) => void;
  /** Required. Unique key for this table — used as localStorage prefix for column preferences. */
  tableId: string;
  /** Called with debounced search value when any column has meta.searchable = true. */
  onSearchChange?: (search: string) => void;
  /** Show the Columns toggle button. */
  showColumnToggle?: boolean;
  /** Called when the user clicks the refresh button in the toolbar. */
  onRefresh?: () => void;
}

export function DataTable<TData>({
  columns,
  data,
  isLoading,
  isFetching,
  pagination,
  sorting,
  emptyState,
  enableRowSelection,
  onRowSelectionChange,
  getRowId,
  onRowClick,
  tableId,
  onSearchChange,
  showColumnToggle = false,
  onRefresh,
}: DataTableProps<TData>) {
  const { role, userId } = usePermissions();
  const isMobile = useIsTableMobile();
  const { columnVisibility, setColumnOverride, resetToDefaults, userOverrides } =
    useColumnVisibility({ tableId, userId, role, columns });

  const hasSearch = columns.some((c) => c.meta?.searchable);
  const { searchValue, inputValue, setInputValue, clearSearch } = useTableSearch();

  useEffect(() => {
    onSearchChange?.(searchValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const [sortingState, setSortingState] = useState<SortingState>(() =>
    sorting?.sortBy ? [{ id: sorting.sortBy, desc: (sorting.sortOrder ?? 'desc') === 'desc' }] : [],
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});

  const totalPages = Math.ceil(pagination.total / pagination.pageSize);

  const table = useReactTable({
    data,
    columns,
    manualPagination: true,
    manualSorting: true,
    pageCount: totalPages,
    enableRowSelection: enableRowSelection ?? false,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getRowId,
    state: {
      sorting: sortingState,
      pagination: { pageIndex: pagination.page - 1, pageSize: pagination.pageSize },
      rowSelection,
      columnVisibility,
      columnSizing,
    },
    onColumnVisibilityChange: () => {
      // no-op — column visibility is managed by useColumnVisibility
    },
    onColumnSizingChange: setColumnSizing,
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sortingState) : updater;
      setSortingState(next);
      if (next.length > 0) {
        sorting?.onSortChange(next[0].id, next[0].desc ? 'desc' : 'asc');
      } else {
        sorting?.onSortChange(undefined, undefined);
      }
    },
    onRowSelectionChange: (updater) => {
      const next = typeof updater === 'function' ? updater(rowSelection) : updater;
      setRowSelection(next);
      if (onRowSelectionChange) {
        const selectedRows = table
          .getRowModel()
          .rows.filter((r) => next[r.id])
          .map((r) => r.original);
        onRowSelectionChange(selectedRows);
      }
    },
    getCoreRowModel: getCoreRowModel(),
  });

  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;
  const colCount = table.getVisibleLeafColumns().length;
  const skeletonCount = isMobile ? Math.min(pagination.pageSize, 5) : pagination.pageSize;

  // Only mask rows with skeleton when a search is loading and there are stale rows to hide.
  // When rows is empty (0-result search + background window-focus refetch), let the empty
  // state through — isFetching would otherwise permanently block it.
  const showSearchSkeleton = !!searchValue && !!isFetching && rows.length > 0;

  const effectiveEmptyState = searchValue
    ? {
        title: 'No results found',
        description: `No results match your search. Try different keywords.`,
        action: (
          <Button variant="outline" size="sm" onClick={clearSearch}>
            Reset search
          </Button>
        ),
      }
    : emptyState;

  const showToolbar = hasSearch || showColumnToggle || !!onRefresh;

  return (
    <div>
      {showToolbar && (
        <DataTableToolbar
          columns={columns}
          hasSearch={hasSearch}
          searchInputValue={inputValue}
          onSearchChange={setInputValue}
          onSearchClear={clearSearch}
          showColumnToggle={showColumnToggle}
          userOverrides={userOverrides}
          columnVisibility={columnVisibility}
          onColumnToggle={setColumnOverride}
          onColumnReset={resetToDefaults}
          onRefresh={onRefresh}
          isFetching={isFetching}
        />
      )}

      {isMobile ? (
        <div>
          {isLoading || showSearchSkeleton ? (
            <div className="space-y-3">
              {Array.from({ length: skeletonCount }).map((_, i) => (
                <div
                  key={i}
                  className="bg-card border border-border/40 rounded-xl shadow-sm p-4 space-y-2"
                >
                  <Skeleton className="h-4 w-3/4 rounded" />
                  <Skeleton className="h-3 w-1/2 rounded" />
                </div>
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="bg-card border border-border/40 rounded-xl shadow-sm py-16 text-center">
              <DataTableEmptyContent state={effectiveEmptyState} />
            </div>
          ) : (
            <div
              className={cn(
                'space-y-3',
                isFetching && !isLoading && 'opacity-70 transition-opacity',
              )}
            >
              {rows.map((row) => (
                <DataTableMobileCard key={row.id} row={row} onRowClick={onRowClick} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div
          className={cn(
            'rounded-xl border border-border/40 bg-background/50 backdrop-blur-md overflow-hidden shadow-sm',
            isFetching && !isLoading && 'opacity-70 transition-opacity',
          )}
        >
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-muted/30">
                {headerGroups.map((headerGroup) => (
                  <TableRow key={headerGroup.id} className="hover:bg-transparent border-border/40">
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        style={{ width: header.getSize() }}
                        className={cn('relative py-4', header.column.columnDef.meta?.className)}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanResize() && (
                          <div
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            style={{ userSelect: 'none', touchAction: 'none' }}
                            className="absolute right-0 top-0 h-full w-3 cursor-col-resize group/resizer"
                          >
                            <div
                              className={cn(
                                'absolute right-1 rounded-full transition-all duration-150',
                                header.column.getIsResizing()
                                  ? 'inset-y-0 w-0.5 bg-primary'
                                  : 'inset-y-2 w-px bg-border/70 group-hover/resizer:inset-y-1 group-hover/resizer:w-0.5 group-hover/resizer:bg-primary/60',
                              )}
                            />
                          </div>
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {isLoading || showSearchSkeleton ? (
                  Array.from({ length: pagination.pageSize }).map((_, i) => (
                    <TableRow key={i} className="border-border/40">
                      {Array.from({ length: colCount }).map((_, j) => (
                        <TableCell key={j} className="py-5">
                          <Skeleton className="h-4 w-full rounded" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : rows.length === 0 ? (
                  <TableRow className="border-border/40 hover:bg-transparent">
                    <TableCell colSpan={colCount} className="py-16 text-center">
                      <DataTableEmptyContent state={effectiveEmptyState} />
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow
                      key={row.id}
                      className={cn(
                        'group border-border/40 transition-colors hover:bg-primary/[0.03]',
                        (onRowClick || enableRowSelection) && 'cursor-pointer',
                        row.getIsSelected() && 'bg-primary/[0.05]',
                      )}
                      data-state={row.getIsSelected() ? 'selected' : undefined}
                      onClick={() => {
                        if (enableRowSelection) row.toggleSelected();
                        onRowClick?.(row.original);
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          className={cn('py-5', cell.column.columnDef.meta?.className)}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {!isLoading && <DataTablePagination {...pagination} />}
    </div>
  );
}
